import * as z from "zod";

import type { RestSchema } from "@mountaya/client";
import { MountayaElementsError } from "@mountaya/elements/errors";

import type { ActivitySlug as DataActivitySlug } from "../../data/rules/dataTypes.js";

/**
 * The activity vocabulary, in both of Mountaya's spellings.
 *
 * The REST API names an activity in kebab case (`hiking-and-trail`); the Data API screams it
 * (`HIKING_AND_TRAIL`). Both are public, an itinerary segment carries the first, and every
 * routing or analysis query takes the second, so anything that routes a segment crosses this
 * boundary, and there is exactly one place to cross it. The crossing is the contract's own
 * transform: the Data API member lowercased, each underscore a hyphen.
 *
 * It is a property of the two contracts rather than of any application, which is why it is
 * published: a consumer building their own routing over `@mountaya/client` meets the same
 * boundary on the first query they write.
 */

/**
 * restActivitySlugs is every activity slug of the REST API the contract declares, each keyed by
 * itself.
 *
 * The `satisfies` clause ties it to the generated `RestSchema.ActivitySlug` in both directions: a
 * member the contract adds leaves a missing key, and a key the contract does not declare is an
 * excess property of the literal, so `tsc` fails either way.
 */
const restActivitySlugs = {
  "hiking-and-trail": "hiking-and-trail",
  "professional-trail": "professional-trail",
  running: "running",
  "backcountry-skiing": "backcountry-skiing",
  "ski-touring": "ski-touring",
  "cross-country-skiing": "cross-country-skiing",
  "snowshoe-walking": "snowshoe-walking",
  "gravel-bike": "gravel-bike",
  "mountain-bike": "mountain-bike",
  "road-cycling": "road-cycling",
} as const satisfies { [K in RestSchema.ActivitySlug]: K };

/**
 * restActivitySlugSchema is the set of activity slugs the REST API uses: the kebab-case form
 * an itinerary segment's activity resolves to.
 *
 * Zod-first because the value crosses the REST → Data API boundary: {@link toDataActivity}
 * validates it before mapping, so a caller passing a raw segment activity cannot reach a
 * query with an unmappable value. The members are the contract's `ActivitySlug`, which a test
 * pins in both directions.
 */
export const restActivitySlugSchema = z.enum(restActivitySlugs);

/**
 * RestActivitySlug is one REST activity slug (e.g. `"hiking-and-trail"`), derived from
 * {@link restActivitySlugSchema}.
 */
export type RestActivitySlug = z.infer<typeof restActivitySlugSchema>;

/**
 * toDataActivity maps a REST activity slug to the Data API enum the routing and analysis
 * queries accept: uppercased, each hyphen an underscore.
 *
 * It THROWS on an unknown slug rather than returning null, and that is the useful behaviour
 * at this boundary: the caller is about to issue a query, a query with no activity is not a
 * weaker query but a different one, and a null threaded onwards would surface as a routing
 * result that quietly used the server's default.
 *
 * @param slug - A REST activity slug, as an itinerary segment carries it.
 * @returns The Data API member, such as `"HIKING_AND_TRAIL"` for `"hiking-and-trail"`.
 * @throws {MountayaElementsError} When `slug` is not a member of
 * {@link restActivitySlugSchema}. The message names the slug, and `cause` is the schema's own
 * `ZodError`.
 */
export const toDataActivity = (slug: string): DataActivitySlug => {
  const parsed = restActivitySlugSchema.safeParse(slug);
  if (!parsed.success) {
    throw new MountayaElementsError(
      `toDataActivity: ${JSON.stringify(slug)} is not an activity slug of the REST API; pass one of ${restActivitySlugSchema.options.join(", ")}`,
      { cause: parsed.error },
    );
  }

  // A validated slug's transform is a Data API member, which the test beside this file proves
  // at the type level for every one.
  return parsed.data.toUpperCase().replaceAll("-", "_") as DataActivitySlug;
};

/**
 * toRestActivity maps a Data API activity enum value back to the REST slug: lowercased, each
 * underscore a hyphen. Total rather than throwing, unlike its inverse.
 */
export const toRestActivity = (value: DataActivitySlug): RestActivitySlug =>
  // The same test proves every Data API member's transform is a REST slug.
  value.toLowerCase().replaceAll("_", "-") as RestActivitySlug;

/**
 * isRestActivity reads whether `slug` is an activity slug of the REST API: a lookup in the
 * closed record the enum is built from, which a search row reads at render rate where a parse
 * would build a result object per call.
 */
const isRestActivity = (slug: string): slug is RestActivitySlug =>
  Object.hasOwn(restActivitySlugs, slug);

/**
 * isDataActivity narrows a raw string to a Data API activity slug, so {@link resolveActivity}
 * can map it without an unchecked cast.
 *
 * A value is a Data API activity when its REST spelling is a REST slug and that slug's Data API
 * spelling is the value itself, so `HIKING-AND-TRAIL` and `Hiking_And_Trail` are not. That reads
 * the one list there is rather than a private copy of the Data API's members, which is exactly
 * the kind of list that stops matching in silence.
 */
const isDataActivity = (slug: string): slug is DataActivitySlug => {
  const rest = slug.toLowerCase().replaceAll("_", "-");

  return isRestActivity(rest) && rest.toUpperCase().replaceAll("-", "_") === slug;
};

/**
 * resolveActivity coerces an activity slug to the REST form, tolerating BOTH vocabularies.
 *
 * The Data API's activity enum is declared UPPER_SNAKE and marshals that way on the wire, so
 * `HIKING_AND_TRAIL` is what a Data API search result actually carries, while a REST response
 * carries `hiking-and-trail`. One helper keys activity glyphs for both, and neither caller knows
 * which spelling it holds, which is the whole reason this accepts either. A value that already
 * parses as a REST slug passes straight through; a Data API value maps through the transform;
 * anything unrecognised yields undefined so a caller skips it rather than render a broken glyph.
 *
 * @param slug - An activity slug in either spelling.
 * @returns The REST slug, or undefined for a value neither vocabulary holds.
 */
export const resolveActivity = (slug: string): RestActivitySlug | undefined => {
  if (isRestActivity(slug)) {
    return slug;
  }

  return isDataActivity(slug) ? toRestActivity(slug) : undefined;
};
